import { loadGoogleMapsScript } from './loadGoogleMapsScript'

/**
 * Custom Google Maps loader for google-map-react. Injects the Maps script on
 * demand (it is no longer in the page head) and resolves once the API is ready,
 * instead of loading the API a second time.
 *
 * Readiness is gated on google.maps.places (not just LatLng) because the
 * script uses loading=async, which means the script tag's load event fires
 * when the bootstrap is ready but before libraries listed via ?libraries=
 * have finished loading.
 */

const TIMEOUT_MS = 10000
const POLL_MS = 50

let loadPromise: Promise<typeof google.maps> | null = null

export const isGoogleMapsReady = () =>
  !!window.google?.maps?.places?.AutocompleteService

export const waitForGoogleMaps = (): Promise<typeof google.maps> => {
  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    if (isGoogleMapsReady()) {
      resolve(window.google.maps)
      return
    }

    loadGoogleMapsScript()

    let pollInterval: ReturnType<typeof setInterval> | null = null
    let timeout: ReturnType<typeof setTimeout> | null = null

    timeout = setTimeout(() => {
      if (pollInterval) clearInterval(pollInterval)
      loadPromise = null
      reject(new Error(`Google Maps failed to load within ${TIMEOUT_MS}ms`))
    }, TIMEOUT_MS)

    pollInterval = setInterval(() => {
      if (isGoogleMapsReady()) {
        clearInterval(pollInterval)
        if (timeout) clearTimeout(timeout)
        resolve(window.google.maps)
      }
    }, POLL_MS)
  })

  return loadPromise
}
